// apps/provider/src/services/api/users.js
// User, organization, location, and solution management methods
//
// Methods: updateUser, updateVelaProvider, updateVelaUser, getAllUsers,
//          addVelaProvider, createUserPublic, createUserSolutionPublic,
//          getUserSolution, getUserPublic, getUsers, getOrganizationUsers,
//          getOrganizationUsersPublic, getOrganizationsPublic, createOrganizationPublic,
//          getLocations, getOrganizationLocationsPublic, addLocation, updateLocation, deleteLocation,
//          updateOrganization, updateOrganizationPublic, getOrganization,
//          getOrganizationPublic, getOrganizationLocations, getOrganizationForms,
//          updateUserSolutionSettings
//
// Response types: @prism-ehr/shared → ProviderUser, Organization, OrganizationLocation, UserSolution

/**
 * @returns {Object} Users domain methods (bound to `this` of the composed apiService)
 */
export function createUsersMethods() {
  return {
    // ============ User Management ============

    /**
     * Update user profile and settings.
     *
     * Xano: POST /user
     * Xano file: 358_user_POST.xs
     *
     * @param {Object} userData
     * @returns {Promise<import('@prism-ehr/shared').ProviderUser>}
     */
    async updateUser(userData) {

      const allSoftware = [
        ...Object.values(userData.software_used || {}).flat(),
        ...(userData.custom_software || [])
      ];

      return this.request('/user', {
        method: 'POST',
        body: JSON.stringify({
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          role: userData.role,
          organization_id: userData.organization?.id || '',
          organization_name: userData.organization?.name || '',
          organization_type: userData.organization?.type || '',
          organization_size: userData.organization?.size || '',
          organization_address: {
            line1: userData.organization?.address?.line1 || '',
            line2: userData.organization?.address?.line2 || '',
            city: userData.organization?.address?.city || '',
            state: userData.organization?.address?.state || '',
            zip: userData.organization?.address?.zip || '',
            country: userData.organization?.address?.country || ''
          },
          phone: userData.phone || '',
          title: userData.title || '',
          notification_preferences: userData.notification_preferences || false,
          email_preferences: userData.email_preferences || false,
          onboarding: {
            is_completed: userData.onboarding?.is_completed || false,
            completed_steps: userData.onboarding?.completed_steps || [],
            last_step: userData.onboarding?.last_step || '',
            completed_at: userData.onboarding?.completed_at || null
          },
          name: `${userData.first_name} ${userData.last_name}`,
          ehr_system: userData.organization?.ehr_system || '',
          software_used: allSoftware,
          user_id: userData.id,
          vela_provider_id: userData.vela_provider_id,
          terms: userData.terms || {
            platform_terms: null,
            privacy_policy: null,
            security_agreement: null,
            solution_terms: {}
          },
          location: userData.location || {},
          clinical_specialty: userData.clinical_specialty || null,
          supervision_level: userData.supervision_level || null,
          org_role_id: userData.org_role_id || null,
          location_ids: userData.location_ids || [],
          schedulable_location_ids: userData.schedulable_location_ids || []
        })
      }, 'updateUser');
    },

    /**
     * Minimal PATCH of a user record. Only fields included in `data` are updated
     * (the backend uses raw-input picking). Use for targeted edits that shouldn't
     * go through the heavyweight updateUser / updateVelaUser flows.
     *
     * Xano: PATCH /user/{user_id}
     * Xano file: 1008_user_user_id_PATCH.xs
     *
     * @param {string} userId
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').ProviderUser>}
     */
    async patchUser(userId, data) {
      return this.request(`/user/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Deactivate a user account. Removes from Auth0 and sets is_deactive = true.
     *
     * Xano: POST /user-deactivate
     * Xano file: user-deactivate_POST.xs
     *
     * @param {string} userId
     * @returns {Promise<import('@prism-ehr/shared').ProviderUser>}
     */
    async deactivateUser(userId) {
      return this.request('/user-deactivate', {
        method: 'POST',
        body: JSON.stringify({ user_id: userId })
      });
    },

    /**
     * Activate (or reactivate) a user: creates a fresh Auth0 account, sends the
     * set-password activation email, and clears is_deactive. Used for users
     * created with "Activate later" and for reactivating deactivated users
     * (deactivation deletes the Auth0 account).
     *
     * Xano: POST /user-activate
     * Xano file: user-activate_POST.xs
     *
     * @param {string} userId
     * @param {string|null} [app]
     * @returns {Promise<import('@prism-ehr/shared').ProviderUser>}
     */
    async activateUser(userId, app = null) {
      return this.request('/user-activate', {
        method: 'POST',
        body: JSON.stringify({ user_id: userId, app })
      });
    },

    /**
     * Resend the activation (set-password) email to a user whose Auth0 account
     * already exists. Does NOT create a new Auth0 account.
     *
     * Xano: POST /resend_user_invitation
     * Xano file: resend_user_invitation_POST.xs (staging) / 123_resend_user_invitation_POST.xs (production)
     *
     * @param {string} userId
     * @param {string} [app]
     * @returns {Promise<Object>}
     */
    async resendUserInvitation(userId, app) {
      return this.request('/resend_user_invitation', {
        method: 'POST',
        body: JSON.stringify({ user_id: userId, app })
      });
    },

    /**
     * Update Vela provider profile (eRx prescriber data).
     *
     * Xano: POST /update_vela_provider
     * Xano file: 490_update_vela_provider_POST.xs
     *
     * @param {Object} userData
     * @param {string} [recordAction='U']
     * @returns {Promise<import('@prism-ehr/shared').ProviderUser>}
     */
    async updateVelaProvider(userData, recordAction = 'U') {
      const allSoftware = [
        ...Object.values(userData.software_used || {}).flat(),
        ...(userData.custom_software || [])
      ];

      return this.request('/update_vela_provider', {
        method: 'POST',
        body: JSON.stringify({
          first_name: userData.first_name || '',
          middle_name: userData.middle_name || '',
          last_name: userData.last_name || '',
          suffix: userData.suffix || '',
          role: userData.role,
          vela_provider_id: userData.vela_provider_id,
          surescripts_provider_id: userData.surescripts_provider_id || '',
          record_action: recordAction,
          organization_id: userData.organization?.id || '',
          organization: userData.organization || {},
          phone: userData.phone || '',
          title: userData.title || '',
          email: userData.email || '',
          erx_active: userData.erx_active,
          erx_active_date: userData.erx_active_date,
          erx_deactive_date: userData.erx_deactive_date,
          dea_number: userData.dea_number || '',
          dea_numbers: userData.dea_numbers || [],
          npi_number: userData.npi_number || '',
          credentials: userData.credentials || '',
          supported_erx_services: userData.supported_erx_services || [],
          erx_pin: userData.erx_pin || '',
          identity_verified: userData.identity_verified || null,
          notification_preferences: userData.notification_preferences || false,
          email_preferences: userData.email_preferences || false,
          sms_preferences: userData.sms_preferences || false,
          onboarding: {
            is_completed: userData.onboarding?.is_completed || false,
            completed_steps: userData.onboarding?.completed_steps || [],
            last_step: userData.onboarding?.last_step || '',
            completed_at: userData.onboarding?.completed_at || null
          },
          name: `${userData.first_name} ${userData.last_name}`,
          ehr_system: userData.organization?.ehr_system || '',
          software_used: allSoftware,
          user_id: userData.id,
          terms: userData.terms || {
            platform_terms: null,
            privacy_policy: null,
            security_agreement: null,
            solution_terms: {}
          },
          location: userData.location || {},
          clinical_specialty: userData.clinical_specialty || null,
          supervision_level: userData.supervision_level || null,
          org_role_id: userData.org_role_id || null,
          location_ids: userData.location_ids || [],
          schedulable_location_ids: userData.schedulable_location_ids || [],
          taxonomy_code: userData.taxonomy_code || '',
          licenses: userData.licenses || [],
          license_number: userData.license_number || '',
          provider_designation: userData.provider_designation || '',
          employment_type: userData.employment_type || '',
          hire_date: userData.hire_date || null,
          is_prn: userData.is_prn || false,
          pay_rates: userData.pay_rates || null,
          is_provider: userData.is_provider || false,
          employee_id: userData.employee_id || '',
          medicare_provider_number: userData.medicare_provider_number || '',
          ...(userData.password && { password: userData.password })
        })
      }, 'updateVelaProvider');
    },

    /**
     * Update Vela user (different response type than updateVelaProvider).
     *
     * Xano: POST /update_vela_user
     * Xano file: 566_update_vela_user_POST.xs
     *
     * @param {Object} userData
     * @param {string} [recordAction='U']
     * @returns {Promise<import('@prism-ehr/shared').ProviderUser>}
     */
    async updateVelaUser(userData, recordAction = 'U') {
      const allSoftware = [
        ...Object.values(userData.software_used || {}).flat(),
        ...(userData.custom_software || [])
      ];

      return this.request('/update_vela_user', {
        method: 'POST',
        body: JSON.stringify({
          first_name: userData.first_name || '',
          middle_name: userData.middle_name || '',
          last_name: userData.last_name || '',
          suffix: userData.suffix || '',
          role: userData.role,
          vela_provider_id: userData.vela_provider_id,
          surescripts_provider_id: userData.surescripts_provider_id || '',
          record_action: recordAction,
          organization_id: userData.organization?.id || '',
          organization: userData.organization || {},
          phone: userData.phone || '',
          title: userData.title || '',
          email: userData.email || '',
          erx_active: userData.erx_active,
          erx_active_date: userData.erx_active_date,
          erx_deactive_date: userData.erx_deactive_date,
          dea_number: userData.dea_number || '',
          dea_numbers: userData.dea_numbers || [],
          npi_number: userData.npi_number || '',
          credentials: userData.credentials || '',
          supported_erx_services: userData.supported_erx_services || [],
          identity_verified: userData.identity_verified || null,
          notification_preferences: userData.notification_preferences || false,
          email_preferences: userData.email_preferences || false,
          onboarding: {
            is_completed: userData.onboarding?.is_completed || false,
            completed_steps: userData.onboarding?.completed_steps || [],
            last_step: userData.onboarding?.last_step || '',
            completed_at: userData.onboarding?.completed_at || null
          },
          name: `${userData.first_name} ${userData.last_name}`,
          ehr_system: userData.organization?.ehr_system || '',
          software_used: allSoftware,
          user_id: userData.id,
          terms: userData.terms || {
            platform_terms: null,
            privacy_policy: null,
            security_agreement: null,
            solution_terms: {}
          },
          location: userData.location || {},
          clinical_specialty: userData.clinical_specialty || null,
          supervision_level: userData.supervision_level || null,
          org_role_id: userData.org_role_id || null,
          location_ids: userData.location_ids || [],
          schedulable_location_ids: userData.schedulable_location_ids || [],
          taxonomy_code: userData.taxonomy_code || '',
          licenses: userData.licenses || [],
          license_number: userData.license_number || '',
          provider_designation: userData.provider_designation || '',
          employment_type: userData.employment_type || '',
          hire_date: userData.hire_date || null,
          is_prn: userData.is_prn || false,
          pay_rates: userData.pay_rates || null,
          is_provider: userData.is_provider || false,
          employee_id: userData.employee_id || '',
          medicare_provider_number: userData.medicare_provider_number || '',
          // Only sent when the form carries it (Veradigm PM orgs) — absent key
          // leaves the practitioner mapping untouched, empty string clears it
          ...(userData.pm_abbreviation !== undefined && { pm_abbreviation: userData.pm_abbreviation }),
          ...(userData.password && { password: userData.password })
        })
      }, 'updateVelaProvider');
    },

    /**
     * Get a single user by ID.
     *
     * Xano: GET /user/{user_id}
     * Xano file: user_user_id_GET.xs
     *
     * @param {string} userId
     * @returns {Promise<import('@prism-ehr/shared').ProviderUser>}
     */
    async getUser(userId) {
      return this.request(`/user/${userId}`, {
        method: 'GET'
      });
    },

    /**
     * Get all users for an organization.
     *
     * Xano: GET /user
     * Xano file: 404_user_GET.xs
     *
     * @param {string} organizationId
     * @returns {Promise<import('@prism-ehr/shared').ProviderUser[]>}
     */
    async getAllUsers(organizationId) {
      return this.request(`/user?organization_id=${organizationId}&include_patients=true`, {
        method: 'GET'
      });
    },

    /**
     * Add a new user as a prescriber.
     *
     * Xano: POST /add_vela_provider
     * Xano file: 463_add_vela_provider_POST.xs
     *
     * @param {Object} userData
     * @returns {Promise<import('@prism-ehr/shared').ProviderUser>}
     */
    async addVelaProvider(userData) {
      return this.request('/add_vela_provider', {
        method: 'POST',
        body: JSON.stringify(userData)
      }, 'addVelaProvider');
    },

    /**
     * Create a new user via public endpoint (sign-up).
     *
     * Xano: POST /public/users
     * Xano file: 915_public_users_POST.xs
     *
     * @param {Object} userData
     * @returns {Promise<import('@prism-ehr/shared').ProviderUser>}
     */
    async createUserPublic(userData) {
      return this.publicRequest('/public/users', {
        method: 'POST',
        body: JSON.stringify(userData)
      }, 'userSignUp');
    },

    /**
     * Create a user solution association via public endpoint.
     *
     * Xano: POST /public/user_solution
     * Xano file: 916_public_user_solution_POST.xs
     *
     * @param {Object} solutionData
     * @returns {Promise<import('@prism-ehr/shared').UserSolution>}
     */
    async createUserSolutionPublic(solutionData) {
      return this.publicRequest('/public/user_solution', {
        method: 'POST',
        body: JSON.stringify(solutionData)
      }, 'userSolutionCreated');
    },

    /**
     * Get a user's solution configuration.
     *
     * Xano: GET /user_solution
     * Xano file: 371_user_solution_GET.xs
     *
     * @param {string} organizationId
     * @param {string} solutionId
     * @param {string} userId
     * @returns {Promise<import('@prism-ehr/shared').UserSolution>}
     */
    async getUserSolution(organizationId, solutionId, userId) {
      return this.request(`/user_solution?organization_id=${organizationId}&solution_id=${solutionId}&user_id=${userId}`, {
        method: 'GET'
      });
    },

    /**
     * Get all active solutions for a user in an organization (single list call).
     *
     * Xano: GET /user_solution (without solution_id returns {type: "list"})
     *
     * @param {string} organizationId
     * @param {string} userId
     * @returns {Promise<import('@prism-ehr/shared').UserSolution[]>}
     */
    async getUserSolutions(organizationId, userId) {
      return this.request(`/user_solution?organization_id=${organizationId}&user_id=${userId}`, {
        method: 'GET'
      });
    },

    /**
     * Get a user's public profile.
     *
     * Xano: GET /user{userId}/public
     * Xano file: 811_user_user_id_public_GET.xs
     *
     * @param {string} userId
     * @returns {Promise<import('@prism-ehr/shared').ProviderUser>}
     */
    async getUserPublic(userId) {
      return this.publicRequest(`/user${userId}/public`, {
        method: 'GET'
      });
    },

    /**
     * Get users including Veradigm providers.
     *
     * Xano: GET /erx/user
     * Xano file: 558_erx_user_GET.xs
     *
     * @param {string} organizationId
     * @param {string} userId
     * @returns {Promise<import('@prism-ehr/shared').ProviderUser[]>}
     */
    async getUsers(organizationId, userId) {
      return this.request(`/erx/user?organization_id=${organizationId}&user_id=${userId}`, {
        method: 'GET'
      });
    },

    /**
     * Get all users for an organization.
     *
     * Xano: GET /user
     * Xano file: 404_user_GET.xs
     *
     * @param {string} organizationId
     * @param {Object} [options]
     * @param {boolean} [options.includePatients=false] - Include patient portal accounts
     * @returns {Promise<import('@prism-ehr/shared').ProviderUser[]>}
     */
    async getOrganizationUsers(organizationId, { includePatients = false } = {}) {
      const params = `organization_id=${organizationId}${includePatients ? '&include_patients=true' : ''}`;
      return this.request(`/user?${params}`, { method: 'GET' });
    },

    /**
     * Get organization users via public endpoint.
     *
     * Xano: GET /users/public
     * Xano file: 808_users_public_GET.xs
     *
     * @param {string} organizationId
     * @returns {Promise<import('@prism-ehr/shared').ProviderUser[]>}
     */
    async getOrganizationUsersPublic(organizationId) {
      return this.publicRequest(`/users/public?organization_id=${organizationId}`, {
        method: 'GET'
      });
    },

    // ============ Organizations ============

    /**
     * Get organizations via public endpoint.
     *
     * Xano: GET /public/organizations
     * Xano file: 913_public_organizations_GET.xs
     *
     * @param {Object} [params]
     * @param {string} [params.name]
     * @param {string} [params.domain]
     * @returns {Promise<import('@prism-ehr/shared').Organization[]>}
     */
    async getOrganizationsPublic(params = {}) {
      const queryParams = new URLSearchParams();
      if (params.name) queryParams.append('name', params.name);
      if (params.domain) queryParams.append('domain', params.domain);
      return this.publicRequest(`/public/organizations?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Create an organization via public endpoint.
     *
     * Xano: POST /public/organizations
     * Xano file: 914_public_organizations_POST.xs
     *
     * @param {Object} orgData
     * @returns {Promise<import('@prism-ehr/shared').Organization>}
     */
    async createOrganizationPublic(orgData) {
      return this.publicRequest('/public/organizations', {
        method: 'POST',
        body: JSON.stringify(orgData)
      });
    },

    /**
     * Update an organization.
     *
     * Xano: PATCH /organization/{organizationId}
     * Xano file: 806_organization_organization_id_PATCH.xs
     *
     * @param {string} organizationId
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').Organization>}
     */
    async updateOrganization(organizationId, data) {
      return this.request(`/organization/${organizationId}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Update an organization via public endpoint.
     *
     * Xano: PATCH /public/organization/{organizationId}
     * Xano file: 917_public_organization_organization_id_PATCH.xs
     *
     * @param {string} organizationId
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').Organization>}
     */
    async updateOrganizationPublic(organizationId, data) {
      return this.publicRequest(`/public/organization/${organizationId}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Get an organization by ID.
     *
     * Xano: GET /organization/{organizationId}
     * Xano file: 360_organization_organization_id_GET.xs
     *
     * @param {string} organizationId
     * @returns {Promise<import('@prism-ehr/shared').Organization>}
     */
    async getOrganization(organizationId) {
      return this.request(`/organization/${organizationId}`, { method: 'GET' });
    },

    /**
     * Get an organization via public endpoint.
     *
     * Xano: GET /public/organizations/{organizationId}
     * Xano file: 987_public_organizations_organization_id_GET.xs
     *
     * @param {string} organizationId
     * @returns {Promise<import('@prism-ehr/shared').Organization>}
     */
    async getOrganizationPublic(organizationId) {
      return this.publicRequest(`/public/organizations/${organizationId}`, { method: 'GET' });
    },

    /**
     * Get organization locations (different endpoint than getLocations).
     *
     * Xano: GET /organization-locations
     * Xano file: 732_organization_locations_GET.xs
     *
     * @param {string} organizationId
     * @returns {Promise<import('@prism-ehr/shared').OrganizationLocation[]>}
     */
    async getOrganizationLocations(organizationId) {
      return this.request(`/organization-locations?organization_id=${organizationId}`, { method: 'GET' });
    },

    /**
     * Get forms for an organization.
     *
     * Xano: GET /organization-forms
     * Xano file: 731_organization_forms_GET.xs
     *
     * @param {string} organizationId
     * @returns {Promise<Object[]>}
     */
    async getOrganizationForms(organizationId) {
      return this.request(`/organization-forms?organization_id=${organizationId}`, { method: 'GET' });
    },

    // ============ Locations ============

    /**
     * Get locations for an organization.
     *
     * Xano: GET /organization_location
     * Xano file: 453_organization_location_GET.xs
     *
     * @param {string} organizationId
     * @returns {Promise<import('@prism-ehr/shared').OrganizationLocation[]>}
     */
    async getLocations(organizationId) {
      return this.request(`/organization_location?organization_id=${organizationId}`, { method: 'GET' });
    },

    /**
     * Public list of an organization's locations. Used by patient-facing forms
     * (LocationSelectField) where there is no auth token.
     *
     * Xano: GET /public/organization-locations
     * Xano file: public_organization-locations_GET.xs
     *
     * @param {string} organizationId
     * @param {{ includeProviders?: boolean }} [options]
     * @returns {Promise<import('@prism-ehr/shared').LocationWithProviders[]>}
     */
    async getOrganizationLocationsPublic(organizationId, { includeProviders = false } = {}) {
      const qp = new URLSearchParams({ organization_id: organizationId });
      if (includeProviders) qp.append('include_providers', 'true');
      return this.publicRequest(`/public/organization-locations?${qp.toString()}`, { method: 'GET' });
    },

    /**
     * Add a new location.
     *
     * Xano: POST /organization_location
     * Xano file: 454_organization_location_POST.xs
     *
     * @param {Object} location
     * @returns {Promise<import('@prism-ehr/shared').OrganizationLocation>}
     */
    async addLocation(location) {
      return this.request('/organization_location', {
        method: 'POST',
        body: JSON.stringify(location)
      });
    },

    /**
     * Update a location.
     *
     * Xano: PATCH /organization_location/{id}
     * Xano file: 876_organization_location_organization_location_id_PATCH.xs
     *
     * @param {string} id
     * @param {Object} location
     * @returns {Promise<import('@prism-ehr/shared').OrganizationLocation>}
     */
    async updateLocation(id, location) {
      return this.request(`/organization_location/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(location)
      });
    },

    /**
     * Delete a location.
     *
     * Xano: DELETE /organization_location/{id}
     * Xano file: 451_organization_location_organization_location_id_DELETE.xs
     *
     * @param {string} id
     * @returns {Promise<null>}
     */
    async deleteLocation(id) {
      return this.request(`/organization_location/${id}`, { method: 'DELETE' });
    },

    // ============ Home Health Facilities ============

    /**
     * @param {string} organizationId
     * @returns {Promise<import('@prism-ehr/shared').HHFacility[]>}
     */
    async getHHFacilities(organizationId) {
      return this.request(`/hh-facility?organization_id=${organizationId}`, { method: 'GET' });
    },

    /**
     * @param {Object} facility
     * @returns {Promise<import('@prism-ehr/shared').HHFacility>}
     */
    async addHHFacility(facility) {
      return this.request('/hh-facility', {
        method: 'POST',
        body: JSON.stringify(facility)
      });
    },

    /**
     * @param {number} id
     * @param {Object} facility
     * @returns {Promise<import('@prism-ehr/shared').HHFacility>}
     */
    async updateHHFacility(id, facility) {
      return this.request(`/hh-facility/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(facility)
      });
    },

    /**
     * @param {number} id
     */
    async deleteHHFacility(id) {
      return this.request(`/hh-facility/${id}`, { method: 'DELETE' });
    },

    // ============ User Solutions ============

    /**
     * Update user solution settings (merges new settings).
     *
     * Xano: PATCH /user_solution/{userSolutionId}
     * Xano file: 373_user_solution_user_solution_id_PATCH.xs
     *
     * @param {string} userSolutionId
     * @param {Object} currentUserSolution
     * @param {Object} newSettings
     * @returns {Promise<import('@prism-ehr/shared').UserSolution>}
     */
    async updateUserSolutionSettings(userSolutionId, currentUserSolution, newSettings) {
      const updatedUserSolution = {
        ...currentUserSolution,
        settings: {
          ...currentUserSolution.settings,
          ...newSettings
        }
      };

      return this.request(`/user_solution/${userSolutionId}`, {
        method: 'PATCH',
        body: JSON.stringify(updatedUserSolution)
      });
    },

    // ============ User Signature ============

    /**
     * Save a user's signature image.
     *
     * Xano: PATCH /user-signature/{userId}
     *
     * @param {string} userId
     * @param {string} signatureDataUrl - Base64 data URL from SignaturePad canvas
     * @returns {Promise<import('@prism-ehr/shared').ProviderUser>}
     */
    async saveUserSignature(userId, signatureDataUrl) {
      return this.request(`/user-signature/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          file_data: signatureDataUrl,
          file_name: 'signature.png',
          file_type: 'image/png',
        })
      });
    },

    /**
     * Remove a user's saved signature.
     *
     * Xano: PATCH /user-signature/{userId}
     *
     * @param {string} userId
     * @returns {Promise<import('@prism-ehr/shared').ProviderUser>}
     */
    async removeUserSignature(userId) {
      return this.request(`/user-signature/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          file_data: null,
          file_name: null,
          file_type: null,
        })
      });
    },

    // ============ Organization Roles ============

    /**
     * Get all roles for an organization.
     *
     * Xano: GET /organization-role
     *
     * @param {string} organizationId
     * @returns {Promise<import('@prism-ehr/shared').OrganizationRoleResponse[]>}
     */
    async getOrganizationRoles(organizationId) {
      return this.request(`/organization-role?organization_id=${organizationId}`, {
        method: 'GET'
      });
    },

    /**
     * Get a single organization role by ID.
     *
     * Xano: GET /organization-role/{role_id}
     *
     * @param {string} roleId
     * @returns {Promise<import('@prism-ehr/shared').OrganizationRoleResponse>}
     */
    async getOrganizationRole(roleId) {
      return this.request(`/organization-role/${roleId}`, {
        method: 'GET'
      });
    },

    /**
     * Create a new organization role.
     *
     * Xano: POST /organization-role
     *
     * @param {Object} roleData
     * @returns {Promise<import('@prism-ehr/shared').OrganizationRoleResponse>}
     */
    async createOrganizationRole(roleData) {
      return this.request('/organization-role', {
        method: 'POST',
        body: JSON.stringify(roleData)
      });
    },

    /**
     * Update an organization role.
     *
     * Xano: PATCH /organization-role/{role_id}
     *
     * @param {string} roleId
     * @param {Object} roleData
     * @returns {Promise<import('@prism-ehr/shared').OrganizationRoleResponse>}
     */
    async updateOrganizationRole(roleId, roleData) {
      return this.request(`/organization-role/${roleId}`, {
        method: 'PATCH',
        body: JSON.stringify(roleData)
      });
    },

    /**
     * Delete an organization role.
     *
     * Xano: DELETE /organization-role/{role_id}
     *
     * @param {string} roleId
     * @returns {Promise<null>}
     */
    async deleteOrganizationRole(roleId) {
      return this.request(`/organization-role/${roleId}`, {
        method: 'DELETE'
      });
    },
  };
}
